// Lays the block grid over the island, then Casa Batlló, Torre Glòries, Hotel W, Arc de Triomf and the office plot.
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { PI } from '../lib/math.js';
import { rnd, rr } from '../lib/random.js';
import { scene } from '../core/scene.js';
import { M, P, add, canvasTex } from '../lib/factory.js';
import { boxW, octShape, slabGeo } from '../lib/shapes.js';
import { instancedRaw } from '../lib/instancing.js';
import { wallMats } from '../materials/walls.js';
import { gloriesMats, pavingMat, stoneSmooth, waterMat } from '../materials/surfaces.js';
import { nightMats } from '../materials/night.js';
import {
  PCH,
  PLATE,
  benches,
  colliders,
  eixampleBlock,
  palmSpots,
  park,
  trees,
} from './eixample.js';

export let streets, casaBatllo, logoTex;

export function buildLandmarks() {
  // ---------- landmarks ----------

  casaBatllo = function casaBatllo(cx, cz, h) {
    const g = new THREE.Group();
    g.position.set(cx, 0.3, cz + 11);
    scene.add(g);
    const H = h + 2.2,
      W = 6.4;
    const mosaic = canvasTex(256, 256, (c) => {
      c.fillStyle = '#e9e1cf';
      c.fillRect(0, 0, 256, 256);
      for (let k = 0; k < 2600; k++) {
        const v = rnd();
        c.fillStyle =
          v < 0.3
            ? '#5f9fc4'
            : v < 0.5
              ? '#7bbf9a'
              : v < 0.62
                ? '#d98a5f'
                : v < 0.72
                  ? '#c9a3d6'
                  : v < 0.8
                    ? '#3e6f9e'
                    : '#f4efe4';
        c.beginPath();
        c.arc(rnd() * 256, rnd() * 256, rr(1.5, 3.5), 0, PI * 2);
        c.fill();
      }
    });
    mosaic.repeat.set(1.5, 2);
    const mos = P(0xffffff, { map: mosaic, roughness: 0.3, metalness: 0.1 }),
      bone = M(0xe8dcc4, { flatShading: false, roughness: 0.5 }),
      dark = M(0x1b1a1d, { flatShading: false });
    add(new THREE.BoxGeometry(W, H, 1.2), wallMats[0], 0, H / 2, -0.1, g);
    const fg = new THREE.PlaneGeometry(W - 0.2, H - 2, 48, 64),
      fp = fg.attributes.position;
    for (let i = 0; i < fp.count; i++) {
      const x = fp.getX(i),
        y = fp.getY(i);
      fp.setZ(i, Math.sin(x * 1.7 + y * 0.4) * 0.09 + Math.sin(y * 1.9) * 0.07);
    }
    fg.computeVertexNormals();
    add(fg, mos, 0, 2 + (H - 2) / 2, 0.72, g);
    // mask-like balconies over oval windows
    for (let y = 2.7; y < H - 1; y += 1.05)
      for (const x of [-2.1, 0, 2.1]) {
        add(new THREE.CircleGeometry(0.3, 24).scale(1, 1.3, 1), dark, x, y, 0.9, g).castShadow =
          false;
        const shell = add(
          new THREE.SphereGeometry(0.4, 24, 12, 0, PI * 2, PI * 0.3, PI * 0.45).scale(
            1.05,
            0.7,
            0.6,
          ),
          bone,
          x,
          y - 0.32,
          0.94,
          g,
        );
        shell.material.side = THREE.DoubleSide;
      }
    // ground floor: organic openings framed by bone columns
    const op = new THREE.Shape();
    op.moveTo(-2.9, 0);
    op.lineTo(2.9, 0);
    op.lineTo(2.9, 1.3);
    for (let i = 12; i >= 0; i--) {
      const x = -2.9 + (i / 12) * 5.8;
      op.lineTo(x, 1.55 + Math.sin(i * 1.3) * 0.18);
    }
    op.closePath();
    add(new THREE.ShapeGeometry(op, 24), dark, 0, 0.02, 0.6, g).castShadow = false;
    const boneG = new THREE.LatheGeometry(
      [
        [0.13, 0],
        [0.17, 0.3],
        [0.11, 0.8],
        [0.16, 1.3],
        [0.1, 1.7],
        [0.18, 1.95],
      ].map(([a, b]) => new THREE.Vector2(a, b)),
      16,
    );
    for (let k = 0; k < 6; k++) add(boneG, bone, -2.6 + k * 1.04, 0, 0.72, g);
    add(new THREE.BoxGeometry(W, 0.2, 0.4), bone, 0, 2.05, 0.7, g);
    // dragon-back roof covered in iridescent scales, with the cross tower
    add(
      new THREE.CylinderGeometry(0.9, 0.9, W - 0.6, 28, 1, false, 0, PI)
        .rotateZ(PI / 2)
        .scale(1, 1, 0.8),
      mos,
      0,
      H,
      -0.1,
      g,
    );
    const scales = [];
    for (let x = -2.85; x <= 2.85; x += 0.26)
      for (let th = 0.12; th < PI - 0.1; th += 0.2) scales.push({ x, th });
    const sc = instancedRaw(
      new THREE.SphereGeometry(0.13, 10, 6).scale(1, 0.45, 1),
      P(0xffffff, { roughness: 0.25, metalness: 0.35 }),
      scales,
      (o, q) => {
        o.position.set(q.x, H + Math.sin(q.th) * 0.92, -0.1 + Math.cos(q.th) * 0.74);
        o.rotation.x = -(q.th - PI / 2);
      },
      (q) =>
        new THREE.Color().setHSL(
          0.33 + q.x * 0.04 + Math.sin(q.th * 3) * 0.05,
          0.55,
          0.45 + Math.cos(q.th) * 0.08,
        ),
    );
    g.add(sc);
    for (let x = -2.8; x <= 2.81; x += 0.4)
      add(
        new THREE.SphereGeometry(0.17, 14, 10),
        P(x > 0 ? 0xc2563a : 0xd9a441, { roughness: 0.3 }),
        x,
        H + 0.98,
        -0.1,
        g,
      );
    add(new THREE.CylinderGeometry(0.3, 0.34, 1.8, 20), mos, -2.5, H + 0.9, 0.25, g);
    add(
      new THREE.SphereGeometry(0.38, 20, 14),
      P(0xd9a441, { roughness: 0.25, metalness: 0.4 }),
      -2.5,
      H + 1.95,
      0.25,
      g,
    );
    for (const [a, b, c] of [
      [0.12, 0.7, 0.12],
      [0.7, 0.12, 0.12],
      [0.12, 0.12, 0.7],
    ])
      add(
        new RoundedBoxGeometry(a, b, c, 2, 0.04),
        P(0xf0e6c8, { roughness: 0.3 }),
        -2.5,
        H + 2.6,
        0.25,
        g,
      );
  };
  // a sign face with a logo image centred on a solid background (the image arrives asynchronously)
  logoTex = function logoTex(src, w, h, bg, pad) {
    const cv = document.createElement('canvas');
    cv.width = w;
    cv.height = h;
    const c = cv.getContext('2d');
    c.fillStyle = bg;
    c.fillRect(0, 0, w, h);
    const tex = new THREE.CanvasTexture(cv);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 4;
    const img = new Image();
    img.onload = () => {
      const k = Math.min((w * (1 - 2 * pad)) / img.width, (h * (1 - 2 * pad)) / img.height),
        iw = img.width * k,
        ih = img.height * k;
      c.drawImage(img, (w - iw) / 2, (h - ih) / 2, iw, ih);
      tex.needsUpdate = true;
    };
    img.src = src;
    return tex;
  };

  // ---------- city grid ----------
  for (let i = -1; i <= 1; i++)
    for (let j = -1; j <= 1; j++) {
      const cx = i * 30,
        cz = j * 30;
      if (!i && !j) continue;
      if (i === 1 && !j)
        park(cx, cz, true); // Plaça de Gaudí, pond facing the Nativity facade
      else if (i === 1 && j === 1) officePlot(cx, cz, 'welocalize');
      else if (i === -1 && j === 1) officePlot(cx, cz, 'aradoxa');
      else eixampleBlock(cx, cz, i === -1 && !j);
    }
  streets = [-45, -15, 15, 45];
  function torreGlories(x, z) {
    const H = 30,
      R = 4.6,
      pts = [];
    for (let i = 0; i <= 48; i++) {
      const t = i / 48,
        r =
          t < 0.62
            ? R * (1 - 0.03 * t)
            : R * 0.98 * Math.sqrt(Math.max(0, 1 - ((t - 0.62) / 0.38) ** 2));
      pts.push(new THREE.Vector2(Math.max(r, 0.02), t * H));
    }
    // façade louvres: red at the base fading to blue at the top, with dark window slits
    const tex = canvasTex(512, 512, (c) => {
      for (let j = 0; j < 96; j++)
        for (let i = 0; i < 128; i++) {
          // mix from the warm palette at the base to the cool palette at the top
          const blue = rnd() < (1 - j / 95) * 1.1 - 0.05,
            hue = blue ? rr(195, 225) : rr(355, 385) % 360;
          c.fillStyle = `hsl(${hue},${blue ? rr(25, 60) : rr(55, 80)}%,${blue ? rr(40, 70) : rr(38, 55)}%)`;
          c.fillRect(i * 4, j * 5.34, 4, 5.4);
          if (rnd() < 0.18) {
            c.fillStyle = 'rgba(15,18,28,.85)';
            c.fillRect(i * 4 + 1, j * 5.34 + 1, 2, 3.4);
          }
        }
    });
    tex.repeat.set(2, 1);
    const gm = P(0xffffff, {
      map: tex,
      emissiveMap: tex,
      emissive: 0xffffff,
      emissiveIntensity: 0,
      roughness: 0.22,
      metalness: 0.35,
    });
    nightMats.push([gm, 1.1]);
    gloriesMats.push(gm);
    add(new THREE.LatheGeometry(pts, 72), gm, x, 0.3, z);
    add(new THREE.RingGeometry(5.4, 7.6, 64).rotateX(-PI / 2), waterMat, x, 0.33, z).castShadow =
      false;
    add(new THREE.TorusGeometry(7.6, 0.12, 8, 64).rotateX(PI / 2), stoneSmooth, x, 0.35, z);
    colliders.push({ type: 'ell', cx: x, cz: z, a: 5, b: 5 });
  }
  function hotelW(x, z) {
    const N = 28,
      pts = [];
    for (let i = 0; i <= N; i++) {
      const u = -1 + (2 * i) / N;
      pts.push([u * 6.5, -(1.9 * (1 - u * u) + 0.3)]);
    }
    for (let i = N; i >= 0; i--) {
      const u = -1 + (2 * i) / N;
      pts.push([u * 6.5, 0.9 * (1 - u * u) + 0.3]);
    }
    const sh = new THREE.Shape();
    sh.moveTo(...pts[0]);
    pts.slice(1).forEach((p) => sh.lineTo(...p));
    sh.closePath();
    const geo = slabGeo(sh, 22),
      gp = geo.attributes.position;
    for (let i = 0; i < gp.count; i++)
      gp.setY(i, gp.getY(i) * (0.84 + 0.16 * Math.cos(((gp.getX(i) / 6.5) * PI) / 2)));
    geo.computeVertexNormals();
    const cw = canvasTex(64, 64, (c) => {
      const gr = c.createLinearGradient(0, 0, 64, 64);
      gr.addColorStop(0, '#9fbfd9');
      gr.addColorStop(1, '#6f93b5');
      c.fillStyle = gr;
      c.fillRect(0, 0, 64, 64);
      c.fillStyle = '#56687a';
      c.fillRect(0, 0, 64, 3);
      c.fillRect(0, 0, 2, 64);
      c.fillRect(32, 0, 1, 64);
    });
    cw.repeat.set(1 / 1.6, 1 / 1.1);
    add(
      geo,
      P(0xffffff, { map: cw, roughness: 0.04, metalness: 0.85, envMapIntensity: 1.4 }),
      x,
      2.3,
      z,
    );
    add(
      new RoundedBoxGeometry(17, 2, 7.5, 3, 0.3),
      M(0xe9e6df, { flatShading: false }),
      x,
      1.3,
      z - 0.3,
    );
    colliders.push({ type: 'rect', x0: x - 8.6, x1: x + 8.6, z0: z - 4.2, z1: z + 3.6 });
  }
  function arcTriomf(x, z) {
    const brick = canvasTex(64, 64, (c) => {
      c.fillStyle = '#b0573c';
      c.fillRect(0, 0, 64, 64);
      c.fillStyle = '#86402d';
      for (let y = 0; y < 64; y += 8) {
        c.fillRect(0, y, 64, 1);
        for (let xx = (y / 8) % 2 ? 0 : 8; xx < 64; xx += 16) c.fillRect(xx, y, 1, 8);
      }
      for (let k = 0; k < 300; k++) {
        c.fillStyle = rnd() < 0.5 ? '#a34f36' : '#c0654a';
        c.fillRect(rnd() * 64, rnd() * 64, 2, 1);
      }
    });
    brick.repeat.set(1 / 1.1, 1 / 1.1);
    const bm = M(0xffffff, { map: brick, flatShading: false }),
      stone = M(0xe6dcc8, { flatShading: false }),
      gold = P(0xd9a441, { roughness: 0.3, metalness: 0.6 });
    const g = new THREE.Group();
    g.position.set(x, 0.3, z);
    scene.add(g);
    const W = 7.2,
      H = 8.4,
      D = 2.6,
      aw = 3.2,
      ah = 3.9,
      sh = new THREE.Shape();
    sh.moveTo(-W / 2, 0);
    sh.lineTo(-aw / 2, 0);
    sh.lineTo(-aw / 2, ah);
    sh.absarc(0, ah, aw / 2, PI, 0, true);
    sh.lineTo(aw / 2, 0);
    sh.lineTo(W / 2, 0);
    sh.lineTo(W / 2, H);
    sh.lineTo(-W / 2, H);
    sh.closePath();
    add(
      new THREE.ExtrudeGeometry(sh, {
        depth: D,
        bevelEnabled: true,
        bevelSize: 0.05,
        bevelThickness: 0.05,
        bevelSegments: 2,
        curveSegments: 24,
      }).translate(0, 0, -D / 2),
      bm,
      0,
      0,
      0,
      g,
    );
    for (const s of [-1, 1])
      add(
        new THREE.TorusGeometry(aw / 2 + 0.05, 0.14, 8, 32, PI),
        stone,
        0,
        ah,
        s * (D / 2 + 0.06),
        g,
      );
    add(new THREE.BoxGeometry(W + 0.2, 0.7, D + 0.2), stone, 0, 6.1, 0, g);
    add(new THREE.BoxGeometry(W + 0.45, 0.28, D + 0.45), stone, 0, H, 0, g);
    add(new THREE.BoxGeometry(W - 1.4, 1.1, D - 0.5), bm, 0, H + 0.65, 0, g);
    for (const sx of [-1, 1])
      for (const sz of [-1, 1]) {
        add(
          new THREE.CylinderGeometry(0.34, 0.38, 1.5, 16),
          stone,
          sx * (W / 2 - 0.4),
          H + 0.85,
          sz * (D / 2 - 0.4),
          g,
        );
        add(
          new THREE.SphereGeometry(0.3, 16, 12),
          gold,
          sx * (W / 2 - 0.4),
          H + 1.8,
          sz * (D / 2 - 0.4),
          g,
        );
      }
    add(new THREE.SphereGeometry(0.45, 20, 14), gold, 0, H + 1.5, 0, g);
    colliders.push(
      { type: 'rect', x0: x - W / 2, x1: x - aw / 2, z0: z - D / 2, z1: z + D / 2 },
      { type: 'rect', x0: x + aw / 2, x1: x + W / 2, z0: z - D / 2, z1: z + D / 2 },
    );
    for (const sx of [-1, 1])
      for (const dz of [4.5, 8.5]) palmSpots.push({ x: x + sx * 4.2, z: z + dz });
  }
  function officePlot(cx, cz, kind) {
    add(slabGeo(octShape(PLATE, PCH), 0.3, 0.06), pavingMat, cx, 0, cz);
    const big = kind === 'welocalize',
      W = big ? 13 : 11,
      D = big ? 10 : 8.5,
      H = big ? 15 : 7.6,
      oz = cz - 2;
    const glassTex = canvasTex(64, 64, (c) => {
      const gr = c.createLinearGradient(0, 0, 0, 64);
      gr.addColorStop(0, big ? '#8fb3d1' : '#a9c2cf');
      gr.addColorStop(1, big ? '#5f82a6' : '#7d98a8');
      c.fillStyle = gr;
      c.fillRect(0, 0, 64, 64);
      c.fillStyle = big ? '#2e3b4a' : '#f2f2ee';
      c.fillRect(0, 48, 64, 16);
      c.fillRect(0, 0, 3, 64);
      c.fillRect(31, 0, 2, 48);
    });
    glassTex.repeat.set(1 / 1.4, 1 / (big ? 1.1 : 1.2));
    const litTex = canvasTex(256, 256, (c) => {
      c.fillStyle = '#000';
      c.fillRect(0, 0, 256, 256);
      for (let j = 0; j < 16; j++)
        for (let i = 0; i < 16; i++)
          if (rnd() < 0.55) {
            c.fillStyle = rnd() < 0.5 ? '#fff4dc' : '#dfefff';
            c.fillRect(i * 16 + 2, j * 16 + 2, 12, 9);
          }
    });
    litTex.repeat.set(1 / 5.6, 1 / (big ? 4.4 : 4.8));
    const gm = P(0xffffff, {
      map: glassTex,
      emissiveMap: litTex,
      emissive: 0xffffff,
      emissiveIntensity: 0,
      roughness: 0.06,
      metalness: 0.55,
      envMapIntensity: 1.3,
    });
    nightMats.push([gm, 0.9]);
    add(boxW(W, H, D), [gm, gm, M(0x9aa0a6), M(0x9aa0a6), gm, gm], cx, 0.3 + H / 2, oz);
    const fins = [];
    for (let y = big ? 1.1 : 1.2; y < H; y += big ? 1.1 : 1.2)
      fins.push(new THREE.BoxGeometry(W + 0.16, 0.07, D + 0.16).translate(0, y, 0));
    add(mergeGeometries(fins), M(0xd9dde0, { flatShading: false }), cx, 0.3, oz);
    add(
      new RoundedBoxGeometry(W * 0.6, 1.2, D * 0.6, 2, 0.1),
      M(0xb9bec4, { flatShading: false }),
      cx,
      0.3 + H + 0.6,
      oz,
    );
    add(
      new THREE.BoxGeometry(5, 0.15, 2.2),
      M(0xe9ecef, { flatShading: false }),
      cx,
      2.55,
      oz + D / 2 + 1,
    );
    for (const sx of [-1, 1])
      add(
        new THREE.CylinderGeometry(0.06, 0.06, 2.2, 10),
        M(0x9aa0a6, { flatShading: false }),
        cx + sx * 2.3,
        1.4,
        oz + D / 2 + 1.9,
      );
    add(
      new THREE.BoxGeometry(2.6, 2, 0.05),
      P(0x1e2733, { roughness: 0.02, metalness: 0.6 }),
      cx,
      1.3,
      oz + D / 2 + 0.03,
    );
    // the company's real logo on the sign: Welocalize on white, Aradoxa's white logo on charcoal
    const st = big
      ? logoTex('assets/logos/welocalize.png', 1024, 184, '#ffffff', 0.1)
      : logoTex('assets/logos/aradoxa.png', 1024, 192, '#26262d', 0.1);
    const sm = M(0xffffff, {
      map: st,
      emissiveMap: st,
      emissive: 0xffffff,
      emissiveIntensity: 0,
      flatShading: false,
    });
    nightMats.push([sm, 1]);
    const edge = M(0x2b2f33);
    add(
      new THREE.BoxGeometry(big ? 7.5 : 5.6, big ? 1.35 : 1.05, 0.18),
      [edge, edge, edge, edge, sm, edge],
      cx,
      big ? 0.3 + H + 1.9 : 0.3 + H - 0.2,
      oz + D / 2 + 0.12,
    );
    colliders.push({
      type: 'rect',
      x0: cx - W / 2 - 0.2,
      x1: cx + W / 2 + 0.2,
      z0: oz - D / 2 - 0.2,
      z1: oz + D / 2 + 2.1,
      top: H + 4,
    });
    for (const [x, z] of [
      [-9, 8],
      [9, 8],
      [-9, -8],
      [9, -8],
      [-9.5, 0],
      [9.5, 0],
    ])
      trees.push({ x: cx + x, z: cz + z, s: rr(0.85, 1.1) });
    for (const [x, z] of [
      [-5.5, 9.6],
      [5.5, 9.6],
    ])
      benches.push({ x: cx + x, z: cz + z, ry: PI });
  }
  arcTriomf(-50, -57);
  torreGlories(52, -54);
  hotelW(-49, 57);
}
